import { triniumApi } from '@api/Trinium/slice';
import { PagedResponse } from '@api/Trinium/types';
import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import {
	City,
	FetchCitiesRequest,
	FetchLocationRequest,
	FetchLocationsRequest,
	PatchLocationRequest,
	PostLocationRequest,
	PutLocationRequest,
	Location,
	LocationListItem,
	transformLocationResponse
} from './types';

export const locationSlice = triniumApi.injectEndpoints({
	endpoints(builder) {
		return {
			fetchLocation: builder.query<Location, FetchLocationRequest>({
				query: ({ terminalNumber, id }) => ({
					url: `/${terminalNumber}/locations/${id}`
				}),
				transformResponse: transformLocationResponse
			}),

			fetchLocations: builder.query<LocationListItem[], FetchLocationsRequest>({
				query: ({ terminalNumber, startsWith }) => ({
					url: `/${terminalNumber}/locations`,
					params: {
						startsWith: startsWith
					}
				})
			}),

			addLocation: builder.mutation<Location, PostLocationRequest>({
				query: ({ terminalNumber, location }) => ({
					url: `/${terminalNumber}/locations`,
					method: 'POST',
					body: location
				}),
				transformResponse: transformLocationResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					const result = await queryFulfilled;

					/** Add Location to the Cache */
					await dispatch(
						locationSlice.util.upsertQueryData(
							'fetchLocation',
							{
								terminalNumber: params.terminalNumber,
								id: result.data.id
							},
							result.data
						)
					);
				}
			}),

			putLocation: builder.mutation<Location, PutLocationRequest>({
				query: ({ terminalNumber, id, actionType }) => ({
					url: `${terminalNumber}/locations/${id}`,
					method: 'PUT',
					body: {
						action: actionType.action,
						data: actionType.data
					}
				}),
				transformResponse: transformLocationResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					const result = await queryFulfilled;
					/** Update the Fetch Location API with the response data */
					await dispatch(
						locationSlice.util.upsertQueryData(
							'fetchLocation',
							{
								terminalNumber: params.terminalNumber,
								id: params.id
							},
							result.data
						)
					);
				}
			}),

			updateLocation: builder.mutation<Location, PatchLocationRequest>({
				query: ({ terminalNumber, location: { id, ...patchValues } }) => ({
					url: `/${terminalNumber}/locations/${id}`,
					method: 'PATCH',
					body: patchValues
				}),
				transformResponse: transformLocationResponse,
				async onQueryStarted(params, { queryFulfilled, dispatch }) {
					const optimisticUpdates = dispatch(
						locationSlice.util.updateQueryData(
							'fetchLocation',
							{
								terminalNumber: params.terminalNumber,
								id: params.location.id
							},
							(draft) => {
								Object.assign(draft, params.location);
							}
						)
					);

					try {
						const result = await queryFulfilled;

						/** Update the Fetch Location API with the response data */
						dispatch(
							locationSlice.util.updateQueryData(
								'fetchLocation',
								{
									terminalNumber: params.terminalNumber,
									id: params.location.id
								},
								(draft) => {
									Object.assign(draft, result.data);
								}
							)
						);
					} catch {
						optimisticUpdates.undo();
					}
				}
			}),

			fetchCities: builder.query<PagedResponse<City>, FetchCitiesRequest>({
				query: ({ contains, page }) => ({
					url: `/cities`,
					params: {
						contains,
						page
					}
				}),
				transformResponse: (response: City[], meta: FetchBaseQueryMeta | undefined) => ({
					items: response,
					hasMore: meta?.response?.headers.get('X-Has-More') === 'true'
				})
			})
		};
	}
});

export const {
	useFetchLocationQuery,
	useFetchLocationsQuery,
	useLazyFetchLocationsQuery,
	useLazyFetchCitiesQuery,
	useAddLocationMutation,
	useUpdateLocationMutation,
	usePutLocationMutation
} = locationSlice;
